/**
 * Legacy ops / governance Super Admin pages kept under Operations & Governance nav.
 * Primary control-plane pages live in dedicated feature files.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";

export function PlatformFailuresPage() {
  const [failures, setFailures] = useState<
    Array<{
      deliveryKey: string;
      accountId: string;
      streamId: string;
      errorCode?: string;
      attempts: number;
    }>
  >([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [confirm, setConfirm] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function refresh() {
    const r = await api<{ failures: typeof failures }>(
      "/api/platform/failures",
    );
    setFailures(r.failures);
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-rs-ink">Failures</h1>
        <Button
          disabled={selected.length === 0}
          onClick={() => setConfirm(true)}
        >
          Redrive Selected
        </Button>
      </div>
      <Panel>
        <ul className="divide-y divide-rs-border">
          {failures.map((f) => (
            <li
              key={f.deliveryKey}
              className="flex items-start gap-3 px-5 py-3 text-sm"
            >
              <input
                type="checkbox"
                checked={selected.includes(f.deliveryKey)}
                onChange={(e) =>
                  setSelected((prev) =>
                    e.target.checked
                      ? [...prev, f.deliveryKey]
                      : prev.filter((k) => k !== f.deliveryKey),
                  )
                }
                aria-label={`Select ${f.deliveryKey}`}
              />
              <div>
                <p className="font-medium text-rs-ink">
                  {f.errorCode ?? "Failed delivery"}
                </p>
                <p className="text-rs-ink-muted">
                  Account {f.accountId} · Stream {f.streamId} · Attempts{" "}
                  {f.attempts}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
      {message ? <p className="text-sm text-rs-ink-muted">{message}</p> : null}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Redrive Failed Deliveries"
        description="Requeue selected failed deliveries for retry across tenant boundaries."
        impact={
          <div>
            <p>Count: {selected.length}</p>
            <p className="mt-1 break-all text-xs">{selected.join(", ")}</p>
          </div>
        }
        confirmLabel="Redrive Deliveries"
        dangerous
        onConfirm={async () => {
          const r = await api<{ redriven: number }>(
            "/api/platform/deliveries/redrive",
            {
              method: "POST",
              body: JSON.stringify({ deliveryKeys: selected }),
            },
          );
          setMessage(`Redriven ${r.redriven} deliveries.`);
          setSelected([]);
          setConfirm(false);
          await refresh();
        }}
      />
    </div>
  );
}

export function PlatformJobsPage() {
  const [jobs, setJobs] = useState<
    Array<{
      id: string;
      accountId: string;
      streamId: string;
      status: string;
      trigger: string;
      errorSummary?: string | null;
      createdAt: number;
    }>
  >([]);

  useEffect(() => {
    void api<{ executions: typeof jobs }>("/api/platform/jobs").then((r) =>
      setJobs(r.executions),
    );
  }, []);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-rs-ink">Jobs / Workers</h1>
      <Panel>
        <ul className="divide-y divide-rs-border">
          {jobs.map((j) => (
            <li key={j.id} className="px-5 py-3 text-sm">
              <div className="flex justify-between gap-2">
                <p className="font-medium text-rs-ink">
                  {j.trigger} · {j.status}
                </p>
                <StatusBadge
                  tone={j.status === "failed" ? "failed" : "syncing"}
                >
                  {j.status}
                </StatusBadge>
              </div>
              <p className="text-rs-ink-muted">
                Account {j.accountId} · Stream {j.streamId}
              </p>
              {j.errorSummary ? (
                <p className="text-rs-warning">{j.errorSummary}</p>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

export function PlatformFlagsPage() {
  const [providers, setProviders] = useState<
    Array<{ provider: string; status: string }>
  >([]);
  const [message, setMessage] = useState<string | null>(null);

  async function refresh() {
    const r = await api<{
      providers: Array<{ provider: string; status: string }>;
    }>("/api/platform/provider-flags");
    setProviders(r.providers ?? []);
  }

  useEffect(() => {
    void refresh().catch(() => setProviders([]));
  }, []);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-rs-ink">Feature Flags</h1>
      <Panel>
        <ul className="divide-y divide-rs-border">
          {providers.map((p) => (
            <li
              key={p.provider}
              className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
            >
              <p className="font-medium capitalize text-rs-ink">{p.provider}</p>
              <select
                className="h-9 rounded-rs-sm border border-rs-border bg-rs-elevated px-2 text-sm"
                value={p.status}
                onChange={(e) => {
                  void api("/api/platform/provider-flags", {
                    method: "PUT",
                    body: JSON.stringify({
                      provider: p.provider,
                      status: e.target.value,
                    }),
                  })
                    .then(() => {
                      setMessage(`Updated ${p.provider}.`);
                      return refresh();
                    })
                    .catch((err) =>
                      setMessage(
                        err instanceof Error ? err.message : "Update failed",
                      ),
                    );
                }}
              >
                <option value="ga">ga</option>
                <option value="beta">beta</option>
                <option value="disabled">disabled</option>
              </select>
            </li>
          ))}
          {providers.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
              No provider flags configured yet.
            </li>
          ) : null}
        </ul>
      </Panel>
      {message ? <p className="text-sm text-rs-ink-muted">{message}</p> : null}
    </div>
  );
}

export function PlatformSettingsPage() {
  const [maintenance, setMaintenance] = useState(false);
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    void api<{ maintenanceMode: boolean; message: string | null }>(
      "/api/platform/maintenance",
    )
      .then((r) => {
        setMaintenance(r.maintenanceMode);
        setMessage(r.message ?? "");
      })
      .catch(() => undefined);
  }, []);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-rs-ink">Platform Settings</h1>
      <Panel className="space-y-3 p-5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={maintenance}
            onChange={(e) => setMaintenance(e.target.checked)}
          />
          Maintenance Mode
        </label>
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Banner message"
        />
        <Button onClick={() => setConfirm(true)}>Save Platform Settings</Button>
        {status ? <p className="text-sm text-rs-ink-muted">{status}</p> : null}
      </Panel>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Update Platform Settings"
        description="This affects all organizations on the platform."
        impact={`Maintenance mode: ${maintenance ? "ON" : "OFF"}. Message: ${message || "(none)"}`}
        confirmLabel="Apply Platform Settings"
        dangerous={maintenance}
        onConfirm={async () => {
          await api("/api/platform/maintenance", {
            method: "POST",
            body: JSON.stringify({ enabled: maintenance, message }),
          });
          setStatus("Saved.");
          setConfirm(false);
        }}
      />
    </div>
  );
}

export function PlatformConferencesPage() {
  const [conferences, setConferences] = useState<
    Array<{
      id: string;
      name: string;
      status: string;
      accountId: string;
      organizationName: string;
      destinations: number;
      billingStatus?: string | null;
    }>
  >([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const statusQ =
      status !== "all" ? `&status=${encodeURIComponent(status)}` : "";
    void api<{ conferences: typeof conferences }>(
      `/api/platform/conferences?q=${encodeURIComponent(q)}${statusQ}`,
    )
      .then((r) => setConferences(r.conferences))
      .catch(() => setConferences([]));
  }, [q, status]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-rs-ink">Conferences</h1>
      <div className="flex flex-wrap gap-2">
        <Input
          className="max-w-sm"
          placeholder="Search conferences or orgs…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {["all", "active", "draft", "past_due", "inactive"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? "primary" : "secondary"}
            onClick={() => setStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>
      <Panel>
        <ul className="divide-y divide-rs-border">
          {conferences.map((c) => (
            <li key={c.id} className="px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-rs-ink">{c.name}</p>
                  <p className="text-rs-ink-muted">
                    <Link
                      className="hover:underline"
                      to={`/superadmin/accounts/${c.accountId}`}
                    >
                      {c.organizationName}
                    </Link>
                    {" · "}
                    {c.destinations} destinations
                  </p>
                </div>
                <StatusBadge
                  tone={
                    c.status === "past_due"
                      ? "warning"
                      : c.status === "active"
                        ? "syncing"
                        : "neutral"
                  }
                >
                  {c.status}
                </StatusBadge>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

export function PlatformOperationsPage() {
  const [executions, setExecutions] = useState<
    Array<{
      id: string;
      status: string;
      trigger: string;
      organizationName?: string;
      conferenceName?: string;
      destinationProvider?: string;
      errorSummary?: string | null;
      createdAt: number;
      examined?: number;
      createdCount?: number;
      updatedCount?: number;
    }>
  >([]);
  const [failures, setFailures] = useState<unknown[]>([]);

  useEffect(() => {
    void api<{ executions: typeof executions; failures: unknown[] }>(
      "/api/platform/operations",
    )
      .then((r) => {
        setExecutions(r.executions);
        setFailures(r.failures);
      })
      .catch(() => {
        setExecutions([]);
        setFailures([]);
      });
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Sync Operations</h1>
        <p className="text-sm text-rs-ink-muted">
          Recent job executions across tenants. {failures.length} open delivery
          failures.
        </p>
      </div>
      <Panel>
        <ul className="divide-y divide-rs-border">
          {executions.map((j) => (
            <li key={j.id} className="px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-rs-ink">
                    {j.conferenceName ?? "Conference"} →{" "}
                    {j.destinationProvider ?? "destination"}
                  </p>
                  <p className="text-rs-ink-muted">
                    {j.organizationName ?? "Organization"} · {j.trigger} ·
                    examined {j.examined ?? 0}
                  </p>
                  {j.errorSummary ? (
                    <p className="text-rs-warning">{j.errorSummary}</p>
                  ) : null}
                </div>
                <StatusBadge
                  tone={j.status === "failed" ? "failed" : "syncing"}
                >
                  {j.status}
                </StatusBadge>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

export function PlatformHealthPage() {
  const [health, setHealth] = useState<{
    components: Record<string, string>;
    metrics: Record<string, number | boolean | null>;
  } | null>(null);

  useEffect(() => {
    void api<typeof health>("/api/platform/health")
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  if (!health) return <PageSkeleton />;

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold text-rs-ink">System Health</h1>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(health.components).map(([name, status]) => (
          <Panel
            key={name}
            className="flex items-center justify-between p-4 text-sm"
          >
            <span className="capitalize text-rs-ink">{name}</span>
            <StatusBadge tone={status === "healthy" ? "ready" : "warning"}>
              {status}
            </StatusBadge>
          </Panel>
        ))}
      </div>
      <Panel>
        <PanelHeader
          title="Operational Metrics"
          description="Last 24 hours where applicable"
        />
        <dl className="grid gap-3 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(health.metrics).map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs uppercase tracking-wide text-rs-ink-subtle">
                {k}
              </dt>
              <dd className="mt-1 text-lg font-semibold text-rs-ink">
                {typeof v === "number" && k.includes("Rate")
                  ? `${Math.round(v * 100)}%`
                  : String(v)}
              </dd>
            </div>
          ))}
        </dl>
      </Panel>
    </div>
  );
}

export function PlatformIntegrationsPage() {
  const [providers, setProviders] = useState<
    Array<{ provider: string; status: string }>
  >([]);
  useEffect(() => {
    void api<{ providers: typeof providers }>("/api/platform/provider-flags")
      .then((r) => setProviders(r.providers ?? []))
      .catch(() => setProviders([]));
  }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-rs-ink">Integrations</h1>
      <p className="text-sm text-rs-ink-muted">
        Provider availability across the platform. Change status under Feature
        Flags.
      </p>
      <Panel>
        <ul className="divide-y divide-rs-border">
          {providers.map((p) => (
            <li
              key={p.provider}
              className="flex items-center justify-between px-5 py-3 text-sm"
            >
              <span className="capitalize font-medium text-rs-ink">
                {p.provider}
              </span>
              <StatusBadge tone={p.status === "disabled" ? "paused" : "ready"}>
                {p.status}
              </StatusBadge>
            </li>
          ))}
          {providers.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
              No providers flagged.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}
